import { CircleMapGeometry, MapGeometry, PolygonMapGeometry, Point } from './interfaces'

type OnGeometryUpdatedHandler = (geometry: MapGeometry) => void

interface GoogleMapsGeometryPathBuilder<TMapGeometry extends MapGeometry> {
  // buildKey(geometry: TMapGeometry): string;
  buildPath(
    geometry: TMapGeometry,
    context: { onGeometryUpdated?: OnGeometryUpdatedHandler; key: string },
    config: { rgbColor: string; editable: boolean }
  ): any
  clear(instance: any): void
}

const circleMapsGeometryBuilder: GoogleMapsGeometryPathBuilder<CircleMapGeometry> = {
  buildPath: ({ radius, center }, { onGeometryUpdated, key }, { rgbColor, editable }) => {
    const googleNS = (window as any).google
    const circle = new googleNS.maps.Circle({
      center,
      radius,
      geodesic: true,
      strokeColor: rgbColor,
      strokeOpacity: 1.0,
      strokeWeight: 1,
      fillColor: rgbColor,
      fillOpacity: 0.1,
      editable
    })

    const onUpdate = () => {
      const { lat, lng } = circle.getCenter()
      onGeometryUpdated?.({
        shape: 'circle',
        center: { lat: lat(), lng: lng() },
        radius: circle.getRadius(),
        key
      })
    }
    googleNS.maps.event.addListener(circle, 'radius_changed', onUpdate)
    googleNS.maps.event.addListener(circle, 'center_changed', onUpdate)
    return circle
  },
  clear: function (path: any) {
    const googleNS = (window as any).google
    googleNS.maps.event.clearInstanceListeners(path)
  }
}

const polygonMapsGeometryBuilder: GoogleMapsGeometryPathBuilder<PolygonMapGeometry> = {
  buildPath: ({ points }, { onGeometryUpdated, key }, { rgbColor, editable }) => {
    const googleNS = (window as any).google
    const polygon = new googleNS.maps.Polygon({
      path: [...points],
      strokeColor: rgbColor,
      strokeOpacity: 1.0,
      strokeWeight: 1,
      fillColor: rgbColor,
      fillOpacity: 0.1,
      editable
    })

    const onUpdate = () => {
      const polygonBounds = polygon.getPath()
      const bounds: Point[] = []
      for (let i = 0; i < polygonBounds.length; i++) {
        const point = {
          lat: polygonBounds.getAt(i).lat(),
          lng: polygonBounds.getAt(i).lng()
        }
        bounds.push(point)
      }
      onGeometryUpdated?.({ shape: 'polygon', points: bounds, key })
    }

    const paths = polygon.getPaths()
    for (let p = 0; p < paths.getLength(); p++) {
      const segment = paths.getAt(p)
      googleNS.maps.event.addListener(segment, 'insert_at', onUpdate)
      googleNS.maps.event.addListener(segment, 'remove_at', onUpdate)
      googleNS.maps.event.addListener(segment, 'set_at', onUpdate)
    }
    return polygon
  },
  clear: function (polygon: any) {
    const googleNS = (window as any).google
    const paths = polygon.getPaths()
    for (let p = 0; p < paths.getLength(); p++)
      googleNS.maps.event.clearInstanceListeners(paths.getAt(p))
  }
}

interface GeometryData {
  geometry: MapGeometry
  instance: any
}

/**
 * Create and recycle Google Maps geometries in a reactive fashion
 */
export class GoogleMapsGeometryManager {
  private geometriesData = new Map<string, GeometryData>()
  private googleMap: any
  private geometryBuilders = {
    circle: circleMapsGeometryBuilder,
    polygon: polygonMapsGeometryBuilder
  }

  constructor(private onGeometryUpdated?: OnGeometryUpdatedHandler) {}

  public setOnGeometryUpdated(handler: OnGeometryUpdatedHandler) {
    this.onGeometryUpdated = handler
  }

  public setMap(map: any) {
    if (this.googleMap != null) throw new Error('You can only set the map once.')
    this.googleMap = map
  }

  public hasMap() {
    return this.googleMap != null
  }

  public updateGeometries(geometries: MapGeometry[]) {
    geometries = geometries.filter((x) => x.shape !== 'none')
    const newGeometriesData = new Map<string, GeometryData>()
    const existingKeys = new Set<string>(geometries.map((x) => x.key))

    for (const geometry of geometries) {
      const existing = this.geometriesData.get(geometry.key)
      if (existing == null) {
        this.registerGeometryData(geometry, newGeometriesData)
      } else {
        const hasChanges = JSON.stringify(existing.geometry) !== JSON.stringify(geometry)
        if (hasChanges) this.refreshGeometryData(existing, geometry)
      }
    }

    for (const [key, pathInstance] of this.geometriesData.entries()) {
      if (!existingKeys.has(key)) this.clearGeometryData(pathInstance)
      else newGeometriesData.set(key, pathInstance)
    }
    this.geometriesData = newGeometriesData
  }

  private buildPath(geometry: MapGeometry) {
    if (geometry.shape === 'none') return null
    const builder = this.geometryBuilders[geometry.shape]
    return builder.buildPath(
      geometry as any,
      { key: geometry.key, onGeometryUpdated: this.onGeometryUpdated },
      {
        rgbColor: geometry.editable === false ? '#666666' : '#FF0000',
        editable: geometry.editable !== false
      }
    )
  }

  private refreshGeometryData(data: GeometryData, updatedGeometry: MapGeometry) {
    if (data.geometry.shape === 'none') return null
    const builder = this.geometryBuilders[data.geometry.shape]
    builder.clear(data.instance)
    data.instance.setMap(null)
    data.instance = this.buildPath(updatedGeometry)
    data.instance.setMap(this.googleMap)
    data.geometry = updatedGeometry
    return
  }

  private clearGeometryData(data: GeometryData) {
    if (data.geometry.shape === 'none') return null
    this.geometryBuilders[data.geometry.shape].clear(data.instance)
    data.instance.setMap(null)
    return
  }

  private registerGeometryData(geometry: MapGeometry, geometriesData: Map<string, GeometryData>) {
    const instance = this.buildPath(geometry)
    instance.setMap(this.googleMap)
    geometriesData.set(geometry.key, { instance, geometry })
  }
}
